// src/components/ThematicEvent.js

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { motion } from "framer-motion";
import { getSkinSrc } from "./SeasonalSkins";
import { getNameStyleCSS } from "./GameNameStyles";
import LoadingSpinner from "./LoadingSpinner";

const API_BASE =
  process.env.REACT_APP_API_URL || "";

/* =========================================================
   HELPERS
   ========================================================= */

function getToken() {
  return localStorage.getItem("token");
}

function authHeaders(json = false) {
  const token = getToken();

  return {
    ...(json
      ? {
          "Content-Type":
            "application/json",
        }
      : {}),

    ...(token
      ? {
          Authorization:
            `Bearer ${token}`,
        }
      : {}),
  };
}

function sleep(ms) {
  return new Promise((resolve) =>
    setTimeout(resolve, ms)
  );
}

function normalizeType(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replaceAll("-", "_")
    .replaceAll(" ", "_");
}

function isSkinItem(item) {
  const type = normalizeType(
    item?.type ??
    item?.itemType ??
    item?.item_type ??
    item?.shopItem?.type ??
    item?.shopItem?.itemType ??
    item?.shopItem?.item_type
  );

  return (
    type === "seasonal_accessory" ||
    type === "skin" ||
    type === "skins"
  );
}


/* =========================================================
   COMPONENT
   ========================================================= */

export default function ThematicEvent({
  user,
  setUser,
}) {
  const scrollRef =
    useRef(null);

  const stepRefs =
    useRef({});

  const [event, setEvent] =
    useState(null);

  const [
    shopItems,
    setShopItems,
  ] = useState([]);

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState("");

  const [
    processingStepId,
    setProcessingStepId,
  ] = useState(null);

  const [
    modalMessage,
    setModalMessage,
  ] = useState(null);


  /* =========================================================
     SHOP ITEM MAP
     ========================================================= */

  const shopItemMap =
    useMemo(() => {
      const map =
        new Map();

      for (
        const item of
        shopItems
      ) {
        if (
          item?.id ===
            null ||
          item?.id ===
            undefined
        ) {
          continue;
        }

        map.set(
          String(item.id),
          item
        );
      }

      return map;
    }, [shopItems]);


  const resolveStepItem =
    useCallback(
      (step) => {
        if (!step) {
          return {};
        }

        const itemId =
          step?.itemId ??
          step?.item_id ??
          null;

        const shopItem =
          itemId !== null
            ? shopItemMap.get(
                String(itemId)
              )
            : null;

        if (!shopItem) {
          return {
            ...step,
          };
        }

        return {
          ...shopItem,
          ...step,
          shopItem,
        };
      },
      [shopItemMap]
    );


  /* =========================================================
     DEMO CARD — SAME IDEA AS SHOP
     ========================================================= */

  const demoPreviewCard =
    useMemo(() => {
      const side =
        String(
          user?.side ||
          user?.alignment ||
          "sun"
        )
          .trim()
          .toLowerCase();

      const parts = [
        "background",
        "body",
        "boots",
        "eyes",
        "face",
        "head",
        "mouth",
        "neck",
        "pants",
        "shirt",
        "weapons",
      ];

      const character = {};

      parts.forEach((part) => {
        character[part] = {
          side,
          rarity:
            "Common",
          variant: 1,
        };
      });

      return {
        level: 1,
        rarity:
          "Common",
        attack: 3,
        defense: 2,
        health: 18,
        character,
      };
    }, [
      user?.side,
      user?.alignment,
    ]);


  /* =========================================================
     SKIN RENDERER
     ========================================================= */

  const renderSkinPreview =
    useCallback(
      (
        rawStep,
        size = 160
      ) => {
        const item =
          resolveStepItem(
            rawStep
          );

        const seasonName =
          item?.season_name ??
          item?.seasonName ??
          item?.shopItem
            ?.season_name ??
          item?.shopItem
            ?.seasonName ??
          null;

        if (!seasonName) {
          return null;
        }

        const order = [
          "background",
          "body",
          "pants",
          "shirt",
          "boots",
          "neck",
          "head",
          "face",
          "eyes",
          "mouth",
          "weapons",
        ];

        return (
          <div
            style={{
              position:
                "relative",

              width:
                size,

              height:
                size,

              borderRadius:
                0,

              overflow:
                "visible",

              background:
                "transparent",

              border:
                0,

              boxShadow:
                "none",

              flex:
                "0 0 auto",
            }}
          >
            {Object.entries(
              demoPreviewCard
                .character ||
                {}
            )
              .sort(
                ([a], [b]) => {
                  const indexA =
                    order.includes(
                      a
                    )
                      ? order.indexOf(
                          a
                        )
                      : order.length;

                  const indexB =
                    order.includes(
                      b
                    )
                      ? order.indexOf(
                          b
                        )
                      : order.length;

                  return (
                    indexA -
                    indexB
                  );
                }
              )
              .map(
                ([
                  part,
                  data,
                ]) => {
                  if (
                    part ===
                    "background"
                  ) {
                    return null;
                  }

                  const defaultSrc =
                    `/assets/${part}/${data.side}/${data.rarity}/${data.variant}_thumb.png`;

                  const src =
                    getSkinSrc(
                      seasonName,
                      part,
                      defaultSrc
                    );

                  return (
                    <img
                      key={
                        part
                      }
                      src={
                        src
                      }
                      alt={
                        part
                      }
                      draggable={
                        false
                      }
                      style={{
                        position:
                          "absolute",

                        inset:
                          0,

                        width:
                          "100%",

                        height:
                          "100%",

                        objectFit:
                          "contain",

                        imageRendering:
                          "pixelated",

                        userSelect:
                          "none",

                        pointerEvents:
                          "none",
                      }}
                      onError={(
                        e
                      ) => {
                        if (
                          e
                            .currentTarget
                            .src !==
                          defaultSrc
                        ) {
                          e.currentTarget.src =
                            defaultSrc;
                        }
                      }}
                    />
                  );
                }
              )}
          </div>
        );
      },
      [
        demoPreviewCard,
        resolveStepItem,
      ]
    );


  /* =========================================================
     NORMAL ITEM ICON
     ========================================================= */

const getNameCategory = (item) => {
  const raw = String(
    item?.name_category ??
    item?.nameCategory ??
    item?.shopItem?.name_category ??
    item?.shopItem?.nameCategory ??
    ""
  ).toLowerCase().trim();

  if (raw === "styles" || raw === "style") return "styles";
  if (raw === "emogy" || raw === "emoji" || raw === "emojy") return "emogy";
  if (raw === "banners" || raw === "banner") return "banners";

  return "other";
};

const getNamePreviewImage = (item) => {
  const value = String(
    item?.name_value ??
    item?.nameValue ??
    item?.image ??
    item?.icon ??
    item?.shopItem?.name_value ??
    item?.shopItem?.nameValue ??
    item?.shopItem?.image ??
    item?.shopItem?.icon ??
    ""
  ).trim();

  if (!value) {
    const category = getNameCategory(item);

    if (category === "banners") return "/banner_icon.png";
    if (category === "emogy") return "/emogy_icon.png";

    return "/style_icon.png";
  }

  if (
    value.startsWith("/") ||
    value.startsWith("http://") ||
    value.startsWith("https://") ||
    value.startsWith("data:")
  ) {
    return value;
  }

  return `/name-cosmetics/${value}`;
};

const getItemImage = useCallback(
  (rawStep) => {
    const item = resolveStepItem(rawStep);

    const type = normalizeType(
  rawStep?.type ??
  rawStep?.itemType ??
  rawStep?.item_type ??
  item?.type ??
  item?.itemType ??
  item?.item_type ??
  item?.shopItem?.type ??
  item?.shopItem?.itemType ??
  item?.shopItem?.item_type
);

    const name = String(
      item?.name ??
      item?.display_name ??
      item?.displayName ??
      rawStep?.name ??
      ""
    ).toLowerCase();

    const fullItemText = JSON.stringify(item || {}).toLowerCase();

    // CHEST — use the specific image configured for this item
if (type === "chest") {
  const chestImage =
    item?.shopItem?.image_url ||
    item?.image_url ||
    rawStep?.image_url;

  return chestImage || "/pack.png";
}

    // CARD PIECES precisa vir antes de CARD
    if (
      type === "card_pieces" ||
      type === "cardpieces" ||
      type === "card_piece" ||
      name.includes("card pieces") ||
      name.includes("card fragment")
    ) {
      return "/pieces.png";
    }

    const isCard =
      type === "card" ||
      type === "cards" ||
      type === "character" ||
      type === "player_card" ||
      type.includes("_card") ||
      type.includes("card_") ||
      name.includes(" card") ||
      name.startsWith("card ") ||
      fullItemText.includes('"type":"card"') ||
      fullItemText.includes('"item_type":"card"');

    if (isCard) {
      if (fullItemText.includes("legendary")) return "/legendary.png";
      if (fullItemText.includes("epic")) return "/epic.png";
      if (fullItemText.includes("rare")) return "/rare.png";

      return "/common.png";
    }

    switch (type) {
      case "runes":
      case "rune":
        return "/runes.png";

      case "sparks":
      case "spark":
        return "/sparks.png";

      case "booster":
      case "boosters":
        return "/energy.png";

      case "tapi":
        return "/icon_tapi.png";

      case "seasonal_accessory":
        return "/fragment.png";

      case "ticket":
      case "tickets":
        return "/ticket.png";

      case "ability":
      case "abilities":
        return "/fightlog.png";

      case "shield":
        return "/shield.png";

      case "spins":
      case "spin":
        return "/fortuneicon.png";

     case "evolve":
  return "/evolveicon.png";

case "name":
  return getNamePreviewImage(item);

default:
  return "/pack.png";
    }
  },
  [resolveStepItem]
);


const renderNameStyleSample = (item, fontSize = 24) => {
  const styleValue = String(
    item?.name_value ??
    item?.nameValue ??
    item?.shopItem?.name_value ??
    item?.shopItem?.nameValue ??
    ""
  ).trim();

  return (
    <div
      style={{
        maxWidth: "100%",
        fontSize,
        lineHeight: 1.1,
        fontWeight: 800,
        textAlign: "center",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        ...getNameStyleCSS(styleValue),
      }}
    >
      TapTopia
    </div>
  );
};

  /* =========================================================
     REWARD VISUAL
     ========================================================= */

  const renderRewardVisual =
    useCallback(
      (
        rawStep,
        {
  skinSize = 100,
  maxWidth = "92%",
  maxHeight = "92%",
  minWidth,
  minHeight,
} = {}
      ) => {
        const item = resolveStepItem(rawStep);

const itemType = normalizeType(
  rawStep?.type ??
  rawStep?.itemType ??
  rawStep?.item_type ??
  item?.type ??
  item?.itemType ??
  item?.item_type ??
  item?.shopItem?.type ??
  item?.shopItem?.itemType ??
  item?.shopItem?.item_type ??
  ""
);

const nameCategory = itemType === "name" ? getNameCategory(item) : null;

if (itemType === "name" && nameCategory === "styles") {
  return renderNameStyleSample(item, 24);
}

if (isSkinItem(item)) {
          const skin =
            renderSkinPreview(
              rawStep,
              skinSize
            );

          if (skin) {
            return skin;
          }
        }

        const src =
          getItemImage(
            rawStep
          );

        return (
          <img
            src={src}
            alt={
              rawStep?.name ||
              item?.name ||
              ""
            }
            draggable={false}
            style={{
  display: "block",
  minWidth,
  minHeight,
  maxWidth,
  maxHeight,
  objectFit: "contain",
}}
            onError={(
              e
            ) => {
              e.currentTarget.onerror =
                null;

              e.currentTarget.src =
                "/pack.png";
            }}
          />
        );
      },
      [
        getItemImage,
        renderSkinPreview,
        resolveStepItem,
      ]
    );


  /* =========================================================
     MODAL
     ========================================================= */

  const showMessage = (
    title,
    text,
    type = "error"
  ) => {
    setModalMessage({
      title,
      text,
      type,
    });
  };

  const closeMessage = () => {
    setModalMessage(null);
  };


  /* =========================================================
     LOAD EVENT + SHOP ITEMS
     ========================================================= */

  const loadEvent =
    useCallback(
      async ({
        silent = false,
      } = {}) => {
        if (!silent) {
          setLoading(true);
        }

        try {
          setError("");

          const [
            eventResponse,
            shopResponse,
          ] =
            await Promise.all([
              fetch(
                `${API_BASE}/api/thematic-events/current`,
                {
                  method:
                    "GET",

                  headers:
                    authHeaders(
                      false
                    ),
                }
              ),

              fetch(
                `${API_BASE}/api/shop/items`,
                {
                  method:
                    "GET",

                  headers:
                    authHeaders(
                      false
                    ),
                }
              ),
            ]);


          const eventData =
            await eventResponse
              .json()
              .catch(
                () => ({})
              );


          const shopData =
            await shopResponse
              .json()
              .catch(
                () => ({})
              );


          console.log(
            "[ThematicEvent] current:",
            eventData
          );

          console.log(
            "[ThematicEvent] shop items:",
            shopData
          );


          if (
            !eventResponse.ok
          ) {
            throw new Error(
              eventData?.error ||
                "Failed to load event"
            );
          }


          if (
            shopResponse.ok
          ) {
            setShopItems(
              Array.isArray(
                shopData?.items
              )
                ? shopData.items
                : []
            );
          } else {
            console.warn(
              "[ThematicEvent] Could not load shop items:",
              shopData
            );
          }


          if (
            !eventData?.event
          ) {
            setEvent(null);

            return null;
          }


          setEvent(
            eventData.event
          );

          return eventData.event;
        } catch (err) {
          console.error(
            "[ThematicEvent] load:",
            err
          );

          setError(
            err?.message ||
              "Failed to load event"
          );

          return null;
        } finally {
          if (!silent) {
            setLoading(false);
          }
        }
      },
      []
    );


  useEffect(() => {
    loadEvent();
  }, [loadEvent]);


  /* =========================================================
     PROGRESSION
     ========================================================= */

  const progression =
    useMemo(() => {
      return Array.isArray(
        event?.progression
      )
        ? event.progression
        : [];
    }, [event]);


  const specialStep =
    progression.length >
    0
      ? progression[
          progression.length -
            1
        ]
      : null;


  const specialIndex =
    progression.length >
    0
      ? progression.length -
        1
      : -1;


  const claimedCount =
    progression.filter(
      (step) =>
        step?.claimed ===
        true
    ).length;


  const totalSteps =
    progression.length;


  const progressPercent =
    totalSteps > 0
      ? Math.max(
          0,
          Math.min(
            100,
            (claimedCount /
              totalSteps) *
              100
          )
        )
      : 0;


  /* =========================================================
     STEP STATE
     ========================================================= */

  const getStepState =
  useCallback(
    (step, index) => {
      if (
        step?.claimed ===
        true
      ) {
        return {
          claimed: true,
          available: false,
          future: false,
          locked: false,
        };
      }

      /*
       * Sequential progression only.
       *
       * Step 1 is immediately available.
       * Every following step requires the
       * previous step to be claimed.
       */
      if (
        index > 0 &&
        progression[
          index - 1
        ]?.claimed !==
          true
      ) {
        return {
          claimed: false,
          available: false,
          future: false,
          locked: true,
        };
      }

      /*
       * Keep server-side/manual lock support,
       * if it is ever used by event config.
       */
      if (
        step?.locked ===
        true
      ) {
        return {
          claimed: false,
          available: false,
          future: false,
          locked: true,
        };
      }

      return {
        claimed: false,
        available: true,
        future: false,
        locked: false,
      };
    },
    [
      progression,
    ]
  );

  /* =========================================================
     USER UPDATE
     ========================================================= */

  const applyUser = (
    data
  ) => {
    if (
      typeof setUser !==
        "function" ||
      !data?.user
    ) {
      return;
    }

    setUser(
      (previous) => ({
        ...previous,
        ...data.user,
      })
    );
  };


  /* =========================================================
     FREE CLAIM
     ========================================================= */

  const claimFree =
    async (step) => {
      const response =
        await fetch(
          `${API_BASE}/api/thematic-events/claim`,
          {
            method:
              "POST",

            headers:
              authHeaders(
                true
              ),

            body:
              JSON.stringify({
                eventId:
                  event.id,

                stepId:
                  step.id,
              }),
          }
        );


      const data =
        await response
          .json()
          .catch(
            () => ({})
          );


      if (!response.ok) {
        throw new Error(
          data?.error ||
            "Failed to claim reward"
        );
      }


      applyUser(data);


      await loadEvent({
        silent:
          true,
      });


      showMessage(
        "SUCCESS!",
        `${step.name || "Reward"} claimed!`,
        "success"
      );
    };


  /* =========================================================
     TELEGRAM ORDER POLLING
     ========================================================= */

  const pollOrder =
    async (orderId) => {
      if (!orderId) {
        return false;
      }


      for (
        let tries = 0;
        tries < 20;
        tries += 1
      ) {
        try {
          const response =
            await fetch(
              `${API_BASE}/api/telegram/stars/order/${orderId}`,
              {
                headers:
                  authHeaders(
                    false
                  ),
              }
            );


          if (
            response.ok
          ) {
            const data =
              await response
                .json()
                .catch(
                  () => ({})
                );


            if (
              data?.status ===
              "paid"
            ) {
              return true;
            }


            if (
              data?.status ===
                "failed" ||
              data?.status ===
                "cancelled"
            ) {
              return false;
            }
          }
        } catch {}


        await sleep(
          1500
        );
      }


      return false;
    };


  /* =========================================================
     TELEGRAM STARS
     ========================================================= */

  const purchaseStars =
    async (step) => {
      const response =
        await fetch(
          `${API_BASE}/api/thematic-events/purchase-stars`,
          {
            method:
              "POST",

            headers:
              authHeaders(
                true
              ),

            body:
              JSON.stringify({
                eventId:
                  event.id,

                stepId:
                  step.id,
              }),
          }
        );


      const data =
        await response
          .json()
          .catch(
            () => ({})
          );


      if (!response.ok) {
        throw new Error(
          data?.error ||
            "Could not start Telegram Stars payment"
        );
      }


      const invoiceLink =
        data?.invoiceLink ??
        data?.payment
          ?.invoiceLink ??
        null;


      const orderId =
        data?.orderId ??
        data?.payment
          ?.orderId ??
        null;


      if (!invoiceLink) {
        throw new Error(
          "Invalid Telegram Stars payment response"
        );
      }


      const telegram =
        window.Telegram
          ?.WebApp;


      if (
        !telegram?.openInvoice
      ) {
        throw new Error(
          "Telegram Stars payments are only available inside Telegram."
        );
      }


      await new Promise(
        (resolve) => {
          telegram.openInvoice(
            invoiceLink,

            async (
              status
            ) => {
              if (
                status !==
                "paid"
              ) {
                if (
                  status ===
                  "failed"
                ) {
                  showMessage(
                    "OOPS!",
                    "Payment failed."
                  );
                }

                resolve();

                return;
              }


              const confirmed =
                orderId
                  ? await pollOrder(
                      orderId
                    )
                  : true;


              await loadEvent({
                silent:
                  true,
              });


              if (
                confirmed
              ) {
                showMessage(
                  "SUCCESS!",
                  `${step.name || "Reward"} claimed!`,
                  "success"
                );
              } else {
                showMessage(
                  "PAYMENT RECEIVED",
                  "The payment was received and is still being confirmed."
                );
              }


              resolve();
            }
          );
        }
      );
    };


  /* =========================================================
     STEP ACTION

     THIS is where locked reward modal belongs.
     ========================================================= */

  const handleStep =
    async (
      step,
      index
    ) => {
      if (
        !step ||
        processingStepId
      ) {
        return;
      }


      const state =
        getStepState(
          step,
          index
        );


      if (
        state.claimed
      ) {
        return;
      }


      if (
        state.locked
      ) {
        showMessage(
          "Reward Locked",
          "Claim previous items before claiming this one!"
        );

        return;
      }


      try {
        setProcessingStepId(
          step.id
        );


        const starsPrice =
          Number(
            step.starsPrice ||
              0
          );


        if (
          starsPrice > 0
        ) {
          await purchaseStars(
            step
          );
        } else {
          await claimFree(
            step
          );
        }
      } catch (err) {
        console.error(
          "[ThematicEvent] action:",
          err
        );


        showMessage(
          "OOPS!",
          err?.message ||
            "Something went wrong."
        );
      } finally {
        setProcessingStepId(
          null
        );
      }
    };


  /* =========================================================
     SPECIAL ITEM TEASER

     IMPORTANT:
     ONLY SCROLLS TO THE FINAL ITEM.
     NEVER OPENS A MODAL.
     ========================================================= */

  const goToSpecialItem = () => {
  if (!specialStep || specialIndex < 0) return;

  const target = stepRefs.current[specialStep.id];
  const scroller = scrollRef.current;

  if (target && scroller) {
    const targetLeft = target.offsetLeft;
    const targetWidth = target.offsetWidth;
    const viewport = scroller.clientWidth;
    const left = targetLeft - viewport / 2 + targetWidth / 2;

    scroller.scrollTo({
      left: Math.max(0, left),
      behavior: "smooth",
    });
  } else if (scroller) {
    scroller.scrollTo({
      left: scroller.scrollWidth,
      behavior: "smooth",
    });
  }

  setTimeout(() => {
    showMessage(
      "Reward Locked",
      "Claim previous items before claiming this one!"
    );
  }, 350);
};

  /* =========================================================
     LOADING
     ========================================================= */

  if (loading) {
  return (
    <div
      className="shop-container"
      style={{
        minHeight: 400,
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxSizing: "border-box",
      }}
    >
      <LoadingSpinner text="Loading Event" size={64} />
    </div>
  );
}


  /* =========================================================
     ERROR
     ========================================================= */

  if (error) {
    return (
      <div
        style={{
          minHeight:
            400,

          padding:
            30,

          display:
            "flex",

          flexDirection:
            "column",

          alignItems:
            "center",

          justifyContent:
            "center",

          gap:
            18,

          textAlign:
            "center",
        }}
      >
        <div>
          {error}
        </div>

        <button
          type="button"
          className="game-button green-button"
          onClick={() =>
            loadEvent()
          }
        >
          TRY AGAIN
        </button>
      </div>
    );
  }


  /* =========================================================
     NO EVENT
     ========================================================= */

  if (!event) {
    return (
      <div
        style={{
          minHeight:
            400,

          display:
            "flex",

          alignItems:
            "center",

          justifyContent:
            "center",

          fontSize:
            20,

          fontWeight:
            900,
        }}
      >
        No thematic event is active.
      </div>
    );
  }


  /* =========================================================
     THEME
     ========================================================= */

     const bannerImage =
  String(
    event?.bannerImageUrl ||
    ""
  ).trim();

  const teaserWidth =
    176;

  const rewardWidth =
    132;

const rewardGap =
  16;

// Vertical spacing for taller screens
const eventVerticalSpacing =
  "clamp(0px, calc((100dvh - 750px) * 0.25), 90px)";


  /* =========================================================
     RENDER
     ========================================================= */

  return (
    <>
      <div
        className="shop-container"
        style={{
          position:
            "relative",

          boxSizing:
            "border-box",

          overflowX:
            "hidden",

          fontFamily:
            "'Fredoka', sans-serif",

         
        }}
      >

        {/* =================================================
            HEADER
            ================================================= */}

        <div
          style={{
            width:
              "100%",

            boxSizing:
              "border-box",

            padding:
              "0 14px",

            textAlign:
              "center",
          }}
        >
          {bannerImage && (
            <img
              src={
                bannerImage
              }
              alt={
                event.name ||
                ""
              }
              draggable={
                false
              }
              style={{
                display:
                  "block",

                width:
                  "auto",

                maxWidth:
                  "72%",

                height:
                  "auto",

                maxHeight:
                  70,

                objectFit:
                  "contain",

                margin:
                  "0 auto 10px",
              }}
              onError={(e) => {
                e.currentTarget.style.display =
                  "none";
              }}
            />
          )}


          {/* BRAND NEW DAY */}

{/* THEME 2 EVENT HEADER */}

<div
  style={{
    position: "relative",
    width: "100%",
    maxWidth: 300,
    aspectRatio: "993 / 244",
    margin: "0 auto 24px",
    backgroundImage: 'url("/theme_2_title.png")',
    backgroundSize: "100% 100%",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    boxSizing: "border-box",
    marginTop: 5
  }}
>
  {/* PROGRESS BAR */}

  <div
    style={{
  position: "absolute",
  left: "8.5%",
  right: "8.8%",
  top: "57.4%",
  height: "26.2%",
  overflow: "hidden",
  borderRadius: 5,
  background: "transparent",
  boxSizing: "border-box",
  padding: 1,
}}
  >
    {/* PROGRESS FILL */}

    <div
      style={{
  position: "relative",
  height: "100%",
  width: `${progressPercent}%`,
  background:
    "linear-gradient(180deg, #ffad2e 0%, #e66a07 100%)",
  borderRadius: 3,
  transition: "width 0.3s ease",
}}
    />

    {/* PROGRESS COUNTER */}

    <div
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#FFFFFF",
        fontFamily: "'Fredoka', sans-serif",
        fontSize: 12,
        lineHeight: "15px",
        fontWeight: 600,
        textShadow: "0 2px 2px #3D1503",
        zIndex: 2,
      }}
    >
      {claimedCount}/{totalSteps}
    </div>
  </div>
</div>
        </div>


        {/* =================================================
            HORIZONTAL TRACK
            ================================================= */}

       <div
  ref={scrollRef}
  style={{
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",

    marginTop: eventVerticalSpacing,

            overflowX:
              "auto",

            overflowY:
              "hidden",

            WebkitOverflowScrolling:
              "touch",

            touchAction:
              "pan-x pan-y",

            overscrollBehaviorX:
              "contain",

            scrollbarWidth:
              "none",

            padding:
              "0 12px 36px",
          }}
        >
          <div
            style={{
              display:
                "flex",

              flexDirection:
                "row",

              flexWrap:
                "nowrap",

              alignItems:
                "flex-start",

              gap:
                rewardGap,

              width:
                "max-content",

              minWidth:
                "max-content",

              paddingRight:
                0,
            }}
          >

            {/* =============================================
                SPECIAL ITEM SHOWCASE

                THIS IS ONLY A SHORTCUT.
                Clicking it does NOT claim.
                Clicking it does NOT show a modal.
                ============================================= */}

            {specialStep && (
  <div
    style={{
      flex:
        `0 0 ${teaserWidth}px`,

      width:
        teaserWidth,

      minWidth:
        teaserWidth,

      paddingTop: 28,

      boxSizing:
        "border-box",
    }}
  >
                <div
                  style={{
  position: "relative",
  isolation: "isolate",
  width: "100%",
  height: 330,
  overflow: "hidden",
  boxSizing: "border-box",

  borderRadius: 7,
  border: "2px solid #75401E",

  backgroundColor: "#42200f",
  backgroundImage: 'url("/theme_2_card_bg.png")',
  backgroundSize: "cover",
  backgroundPosition: "center",
  backgroundRepeat: "no-repeat",

  boxShadow: "0 3px 0 #241007",
}}
                >

                  <div className="shadow1"
                    style={{
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  height: 30,

  display: "flex",
  alignItems: "center",
  justifyContent: "center",

  color: "#FFFFFF",
  background: "linear-gradient(180deg, #96542D, #703716)",

  border: "1px solid #C77A39",
  borderRadius: 5,
  boxSizing: "border-box",

  fontFamily: "'Fredoka', sans-serif",
  fontSize: 15,
  lineHeight: "18px",
  fontWeight: 900,
  textTransform: "uppercase",
  textShadow: "0 2px 2px rgba(0,0,0,0.65)",

  zIndex: 3,
}}
                  >
                    SPECIAL ITEM
                  </div>


                  <div
                    style={{
                      position:
                        "absolute",

                      top:
                        32,

                      left:
                        0,

                      right:
                        0,

                      bottom:
                        0,

                      display:
                        "flex",

                      alignItems:
                        "center",

                      justifyContent:
                        "center",

                      padding:
                        "8px 8px 62px",

                      boxSizing:
                        "border-box",

                      background:
                        "rgba(65,65,65,.35)",
                    }}
                  >
                    {renderRewardVisual(
                      specialStep,
                      {
                        skinSize:
                          220,

                        maxWidth:
                          "100%",

                        maxHeight:
                          "100%",
                      }
                    )}


                    <button
  type="button"
  onClick={goToSpecialItem}
  style={{
    position: "absolute",
    left: 18,
    bottom: 12,
    width: "calc(100% - 36px)",
    aspectRatio: "658 / 219",
    height: "auto",
    minHeight: 0,
    padding: 0,

    display: "flex",
    alignItems: "center",
    justifyContent: "center",

    backgroundImage: 'url("/theme_2_bt_1.png")',
    backgroundSize: "100% 100%",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    backgroundColor: "transparent",
    paddingBottom: 3,

    border: "none",
    borderRadius: 0,
    boxShadow: "none",
    appearance: "none",
    WebkitAppearance: "none",

    fontFamily: "'Fredoka', sans-serif",
    fontSize: 15,
    lineHeight: "13px",
    fontWeight: 900,
    color: "#3D1503",
    textShadow: "none",
    cursor: "pointer",
  }}
>
  GET THE SKIN
</button>
                  </div>
                </div>
              </div>
            )}


            {/* =============================================
                REAL PROGRESSION
                ============================================= */}

            {progression.map(
              (
                step,
                index
              ) => {
                const resolvedItem =
                  resolveStepItem(
                    step
                  );


                const state =
                  getStepState(
                    step,
                    index
                  );


                const price =
                  Math.max(
                    0,
                    Number(
                      step.starsPrice ||
                        0
                    )
                  );


                const busy =
                  processingStepId ===
                  step.id;


                const isLast =
                  index ===
                  progression.length -
                    1;


                const isSkin =
                  isSkinItem(
                    resolvedItem
                  );


                const buttonLabel = price > 0 ? price : "FREE";


                return (
                  <div
                    key={
                      step.id ||
                      index
                    }
                    ref={(
                      element
                    ) => {
                      if (
                        element &&
                        step.id
                      ) {
                        stepRefs.current[
                          step.id
                        ] =
                          element;
                      }
                    }}
                    style={{
                      flex:
                        `0 0 ${rewardWidth}px`,

                      width:
                        rewardWidth,

                      minWidth:
                        rewardWidth,

                      paddingTop:
                        46,

                      boxSizing:
                        "border-box",
                    }}
                  >

                    {/* CARD */}

                   <div
  style={{
    position: "relative",
    width: rewardWidth,
    aspectRatio: "180 / 260",
    height: "auto",
    overflow: "hidden",
    boxSizing: "border-box",
    border: "none",
    borderRadius: 0,

    backgroundColor: "transparent",
    backgroundImage:
  isLast
    ? 'url("/theme_2_card_4.png")'
    : state.claimed
    ? 'url("/theme_2_card_3.png")'
    : price > 0
    ? 'url("/theme_2_card_2.png")'
    : 'url("/theme_2_card_1.png")',

    backgroundSize: "100% 100%",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",

    boxShadow: "none",
    filter: "none",
    opacity: 1,
  }}
>

                      {state.claimed && (
  <img
    src="/theme_2_verify.png?1"
    alt="Claimed"
    draggable={false}
    style={{
      position: "absolute",
      zIndex: 10,
      top: 5,
      right: 5,
      width: 28,
      height: 28,
      objectFit: "contain",
      pointerEvents: "none",
    }}
  />
)}


                      {!state.claimed && (
  <img
    src={price > 0 ? "/theme_1_dot.png" : "/theme_1_plus.png"}
    alt=""
    draggable={false}
    style={{
      position: "absolute",
      zIndex: 10,
      top: 5,
      right: 5,
      width: 28,
      height: 28,
      objectFit: "contain",
      pointerEvents: "none",
      display: 'none'
    }}
  />
)}


                      <div
                        style={{
                          height:
                            169,

                          display:
                            "flex",

                          alignItems:
                            "center",

                          justifyContent:
                            "center",

                          padding:
                            isSkin
                              ? 0
                              : 10,

                          boxSizing:
                            "border-box",
                        }}
                      >
                        {renderRewardVisual(
  step,
  {
    skinSize: isLast ? 132 : 92,
    maxWidth: isSkin ? "92%" : "76%",
    maxHeight: isSkin ? "92%" : "76%",
    minWidth: isSkin ? undefined : 60,
    minHeight: isSkin ? undefined : 60,
  }
)}
                      </div>


                      <div
                        style={{
                          position:
                            "absolute",

                          left:
                            1,

                          right:
                            1,

                          bottom:
                            7,

                          height:
                            32,

                          padding:
                            "2px 3px",

                            paddingTop: 6,

                          boxSizing:
                            "border-box",

                          display:
                            "flex",

                          alignItems:
                            "center",

                          justifyContent:
                            "center",

                          textAlign:
                            "center",

                          background: "#4E1F0E",

                          color:
                            "#fff",

                          fontSize:
                            10,

                          lineHeight:
                            "11px",

                          fontWeight:
                            900,

                          textTransform:
                            "uppercase",

                          textShadow:
                            "0 1px 2px #000",
                        }}
                      >
                        {step.name ||
                          resolvedItem?.name ||
                          `Reward ${
                            index +
                            1
                          }`}
                      </div>
                    </div>


                    {/* CLAIM / BUY */}


{state.claimed ? (
  <img
    src="/theme_2_bt_2.png"
    alt="Claimed"
    draggable={false}
    style={{
      display: "block",
      width: rewardWidth,
      height: "auto",
      aspectRatio: "658 / 219",
      marginTop: 12,
      objectFit: "contain",
      pointerEvents: "none",
    }}
  />
) : (
  <button
    type="button"
    disabled={busy}
    onClick={() => handleStep(step, index)}
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: rewardWidth,
      aspectRatio: "658 / 219",
      height: "auto",
      marginTop: 12,
      padding: "0 6px",
      background: 'url("/theme_2_bt_1.png") center / 100% 100% no-repeat',
      border: "none",
      borderRadius: 0,
      boxShadow: "none",
      cursor: busy ? "wait" : "pointer",
      fontFamily: "'Fredoka', sans-serif",
      fontSize: 15,
      paddingBottom: 6,
      fontWeight: 600,
      color: "#3D1503",
      textShadow: "0 2px 1px rgba(0,0,0,.5)",
      opacity: state.locked ? 0.9 : 1,
      boxSizing: "border-box",
    }}
  >
    {busy ? (
      "..."
    ) : price > 0 ? (
      <span
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 5,
        }}
      >
        {price} &#9733;
      </span>
    ) : (
      buttonLabel
    )}
  </button>
)}


                    {/* PROGRESS TRACK */}

                    <div
                      style={{
                        position:
                          "relative",

                        width:
                          `calc(100% + ${rewardGap}px)`,

                        height:
                          40,

                        marginTop:
                          12,
                      }}
                    >

                      {/* LEFT */}

                     {/* LEFT */}
<div
  style={{
    position: "absolute",
    top: 13,
    right: "50%",
    width:
      index === 0
        ? 0
        : `calc(50% + ${rewardGap / 2}px)`,
    height: 8,
    background:
  progression[index - 1]?.claimed
    ? "#c96a24"
    : "#3f3f3f",
    borderRadius: 999,
  }}
/>


                      {/* RIGHT */}
{!isLast && (
  <div
    style={{
      position: "absolute",
      top: 13,
      left: "50%",
      width: `calc(50% + ${rewardGap / 2}px)`,
      height: 8,
      background:
  state.claimed
    ? "#c96a24"
    : "#3f3f3f",
      borderRadius: 999,
    }}
  />
)}


                      {/* NUMBER */}

                      <div
                        style={{
                          position:
                            "absolute",

                          zIndex:
                            3,

                          top:
                            1,

                          left:
                            "50%",

                          transform:
                            "translateX(-50%)",

                          width:
                            32,

                          height:
                            32,

                          display:
                            "flex",

                          alignItems:
                            "center",

                          justifyContent:
                            "center",

                          boxSizing:
                            "border-box",

                          borderRadius:
                            "50%",

                          border:
                            "3px solid #fff",

                          background:
  state.claimed
    ? "#c96a24"
    : "#565656",

                          color:
                            "#fff",

                          fontSize:
                            12,

                          lineHeight:
                            "12px",

                          fontWeight:
                            900,

                          boxShadow:
                            "0 1px 3px rgba(0,0,0,.45)",
                        }}
                      >
                        {index + 1}
                      </div>

                    </div>
                  </div>
                );
              }
            )}
          </div>
        </div>
      </div>


      {/* =====================================================
          MODAL

          Only real reward interactions invoke this now.
          The showcase NEVER does.
          ===================================================== */}

      {modalMessage && (
        <div className="modal_box">

          <div className="modal_title">
            {modalMessage.title}

            <button
              type="button"
              className="modal_close"
              onClick={
                closeMessage
              }
            />
          </div>


          <motion.div
            className="modal_body"
            initial={{
              scale:
                0.8,

              opacity:
                0,
            }}
            animate={{
              scale:
                1,

              opacity:
                1,
            }}
            exit={{
              scale:
                0.92,

              opacity:
                0,
            }}
          >
            <div className="modal_inside">

              <img
                src="/finger.png"
                alt=""
                style={{
                  height:
                    80,

                  marginBottom:
                    10,

                  transform:
                    modalMessage
                      .type ===
                    "error"
                      ? "rotate(180deg)"
                      : "none",
                }}
              />


              <div
                className="shadow2"
                style={{
                  fontWeight:
                    600,

                  fontSize:
                    24,

                  marginBottom:
                    10,
                }}
              >
                {modalMessage
                  .type ===
                "success"
                  ? "Reward Claimed!"
                  : modalMessage
                      .title ===
                    "SPECIAL ITEM"
                  ? "Special Item"
                  : "The Frankenstein"}
              </div>


              <div
                className="info"
                style={{
                  fontSize:
                    16,
                }}
              >
                {
                  modalMessage.text
                }
              </div>

            </div>


            <div className="modal_footer">

              <button
                type="button"
                className="game-button red-button"
                onClick={
                  closeMessage
                }
              >
                CLOSE
              </button>

            </div>

          </motion.div>
        </div>
      )}
    </>
  );
}